/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useImperativeHandle } from 'react';

import { observer } from 'mobx-react-lite';

import 'mapbox-gl/dist/mapbox-gl.css';
import { elevationService } from '../services/ElevationService';
import { campaignRootStore } from '../stores/CampaignRootStore';
import { MAP_CONSTANTS } from './Mapbox/mapConstants';
import { MapDroneViewProps, MapType, LiveLineData, MarkerData } from './Mapbox/mapTypes';
import { useElevationPreload } from './Mapbox/useElevationPreload';
import { useLiveLineUpdates } from './Mapbox/useLiveLineUpdates';
import { useMapInitialization } from './Mapbox/useMapInitialization';
import { useStaticMarkers } from './Mapbox/useStaticMarkers';
import { useTelemetryMarker } from './Mapbox/useTelemetryMarker';
import { useUserDragTracking } from './Mapbox/useUserDragTracking';

export type { LiveLineData, MarkerData, MapType };

const MapCardView: React.FC<MapDroneViewProps> = observer(
  ({
    liveLine,
    markers,
    mapRef,
    customZoomControls = false,
    initialZoom = MAP_CONSTANTS.DEFAULT_ZOOM,
    hideControls = false,
    inPipMode = false
  }) => {
    const mapContainer = useRef<HTMLDivElement | null>(null);
    const targetZoomRef = useRef<number>(initialZoom);
    const createdTimeRef = useRef<number>(Date.now());
    const { mapStore, telemetryStore } = campaignRootStore;

    const { map, markerManagerRef, mapLoaded } = useMapInitialization(
      mapContainer,
      initialZoom,
      customZoomControls,
      hideControls
    );

    const flyToEnabledRef = useUserDragTracking(map, mapLoaded);

    useEffect(() => {
      targetZoomRef.current = initialZoom;
    }, [initialZoom]);

    useLiveLineUpdates(
      map,
      markerManagerRef,
      liveLine,
      mapLoaded,
      inPipMode,
      targetZoomRef,
      createdTimeRef,
      flyToEnabledRef
    );

    useStaticMarkers(map, markerManagerRef, markers, liveLine, mapLoaded);

    useElevationPreload(liveLine, mapLoaded);

    useTelemetryMarker(
      map,
      mapLoaded,
      mapStore.currentPosition,
      mapStore.prevPosition,
      mapStore.firstDataReceived,
      telemetryStore.telemetryData?.heading_deg,
      mapStore.positionHistory,
      targetZoomRef,
      createdTimeRef,
      inPipMode,
      flyToEnabledRef
    );

    useImperativeHandle<MapType, MapType>(
      mapRef,
      (): MapType => ({
        getElevation: async ([longitude, latitude]: [number, number]): Promise<number | '--'> => {
          if (!map.current || !mapLoaded) return '--';

          try {
            const elevation = await elevationService.getElevation(latitude, longitude);

            if (
              elevation !== null &&
              elevation !== undefined &&
              !Number.isNaN(elevation) &&
              Number.isFinite(elevation)
            ) {
              return Math.round(elevation);
            }

            return '--';
          } catch (error) {
            console.error('Failed to get elevation:', error);
            return '--';
          }
        },
        resize: (): void => {
          if (map.current && mapLoaded) {
            map.current.resize();
          }
        },
        getZoom: (): number | undefined => {
          if (!map.current || !mapLoaded) return undefined;
          return map.current.getZoom();
        }
      })
    );

    return (
      <div className='w-full h-full flex relative'>
        <style>
          {`
          .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib {
            display: none !important;
          }
        `}
        </style>
        <div className='w-full h-full' ref={mapContainer} />
      </div>
    );
  }
);

export default React.memo(MapCardView);
